import { nanoid } from "nanoid";

import type { InterviewerHarnessType } from "@performyard/service-contracts";

import type { DocumentFile } from "../../../pages/Documents/schema";

import type { ChatMessage } from "../types";

export type DocumentModalState = {
	isOpen: boolean;
	document: DocumentFile | null;
};

export const initialDocumentModalState: DocumentModalState = {
	isOpen: false,
	document: null,
};

export type GreetingFlags = {
	askHrEnabled: boolean;
	goalCreationEnabled: boolean;
	formCreationEnabled: boolean;
	interviewerEnabled: boolean;
	reportBuilderEnabled: boolean;
	isOnFormBuilderPage: boolean;
};

export const buildGreetingMessage = (flags: GreetingFlags): ChatMessage => {
	const capabilities: string[] = [];
	if (flags.isOnFormBuilderPage && flags.formCreationEnabled) {
		return {
			id: "greeting",
			role: "assistant",
			content:
				"Hi there! It looks like you're building a form. Is there anything I can assist you with for this form?",
			timestamp: new Date(),
		};
	}
	if (flags.askHrEnabled) {
		capabilities.push("pull accurate, up-to-date answers from your company's internal documents");
	}
	if (flags.goalCreationEnabled) {
		capabilities.push("help you create actionable goals");
	}
	if (flags.formCreationEnabled) {
		capabilities.push("assist with creating review forms for your organization");
	}
	if (flags.interviewerEnabled) {
		capabilities.push("build a competency framework for your organization");
	}
	if (flags.reportBuilderEnabled) {
		capabilities.push("build a Reporting & Analytics report through guided questions");
	}

	let content: string;
	if (capabilities.length === 0) {
		content = "Hi there! How can I help you today?";
	} else if (capabilities.length === 1) {
		content = `Hi there! Need help? I can ${capabilities[0]}.`;
	} else {
		const last = capabilities.pop()!;
		const separator = capabilities.length > 1 ? ", or " : " or ";
		content = `Hi there! Need help? I can ${capabilities.join(", ")}${separator}${last}.`;
	}

	return {
		id: "greeting",
		role: "assistant",
		content,
		timestamp: new Date(),
	};
};

export const generateMessageId = (): string => {
	return `msg-${nanoid()}`;
};

// Session ID helpers — persisted in sessionStorage so it survives full page reloads
// (e.g. React ↔ Template page transitions) within the same tab.
const SESSION_ID_KEY = "ask-py-session-id";

export const getInterviewerNotesKey = (harness: InterviewerHarnessType): string => {
	return `ask-py-${harness}-notes`;
};
export const INTERVIEWER_HARNESS_KEY = "ask-py-interviewer-harness";

export const getActiveInterviewerHarness = (): InterviewerHarnessType | null =>
	sessionStorage.getItem(INTERVIEWER_HARNESS_KEY) as InterviewerHarnessType | null;

export const setActiveInterviewerHarness = (harness: InterviewerHarnessType | null): void => {
	if (harness) {
		sessionStorage.setItem(INTERVIEWER_HARNESS_KEY, harness);
	} else {
		sessionStorage.removeItem(INTERVIEWER_HARNESS_KEY);
	}
};

export const getOrCreateSessionId = (): string => {
	const existing = sessionStorage.getItem(SESSION_ID_KEY);
	if (existing) return existing;
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};

export const resetSessionId = (): string => {
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};
